// 案件AIチャット — 右下フローティングパネル（横断QA・出典チップ）
//
// Claude Design プロトタイプ (_management/designs/ui_prototype/app/ChatPanel.jsx) の
// UI 構造を再現しつつ、ダミー応答を実 Cloud Functions chatWithMinutes に差し替えた版。
// 既存 ProjectChatPage.tsx の呼び出し / エラーハンドリングと同じ契約に揃える
// （PLAN_REQUIRED_STANDARD / PLAN_CHECK_FAILED の出し分け、失敗した質問の入力欄戻し）。
import React, { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Icon, SourceChip } from './primitives';

interface Project {
  id: string;
  name: string;
  meetingCount?: number;
  // S-055-3（DEC-053 案B）: 会社モード。指定時は「そのプロジェクト内の当該会社」に
  // 絞って chatWithMinutes を呼ぶ（projectId 必須は維持・横断はしない）。
  companyId?: string;
}

interface ChatSource {
  sourceMeetingId: string | null;
  projectId: string | null;
  meetingTitle: string | null;
  projectName: string | null;
}

interface ChatResponse {
  text: string;
  sources?: ChatSource[];
  usedMinutesCount?: number;
  truncated?: boolean;
  mode?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: ChatSource[];
}

interface Props {
  open: boolean;
  project: Project;
  onClose: () => void;
  onJump: (meetingId: string) => void;
}

// 履歴として Functions に渡す最大件数（サーバ側 MAX_CHAT_HISTORY_MESSAGES と整合）。
const MAX_HISTORY_TO_SEND = 10;
const MAX_MESSAGE_CHARS = 2000;

// ワンタップ Skill（プロトタイプ準拠。label をそのまま質問文として送る）。
// scopeWord はモードで切替（案件モード=「この案件」/ 会社モード=「この会社」）。
const buildChatSkills = (scopeWord: string): { id: string; icon: string; label: string }[] => [
  { id: 's1', icon: 'circle-help', label: `${scopeWord}の未決事項` },
  { id: 's2', icon: 'git-compare', label: '前回からの変更点' },
  { id: 's3', icon: 'list-checks', label: '次回までのTODO' },
  { id: 's4', icon: 'users', label: 'キーパーソンと役割' },
];

const ChatPanel: React.FC<Props> = ({ open, project, onClose, onJump }) => {
  // 会社モード（companyId 指定）か案件モードかでコピーを切替（DEC-009 ガード継続）。
  const isCompanyMode = !!project.companyId;
  const scopeWord = isCompanyMode ? 'この会社' : 'この案件';
  const CHAT_SKILLS = buildChatSkills(scopeWord);
  const [msgs, setMsgs] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [showSkills, setShowSkills] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planBlocked, setPlanBlocked] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, thinking, error, planBlocked]);

  const ask = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || thinking) return;
    if (trimmed.length > MAX_MESSAGE_CHARS) {
      setError(`メッセージは${MAX_MESSAGE_CHARS}文字以内で入力してください`);
      return;
    }

    const history = msgs
      .slice(-MAX_HISTORY_TO_SEND)
      .map((m) => ({ role: m.role, content: m.content }));

    const userMessage: ChatMessage = { role: 'user', content: trimmed };
    setMsgs((m) => [...m, userMessage]);
    setInput('');
    setShowSkills(false);
    setError(null);
    setPlanBlocked(false);
    setThinking(true);

    try {
      const fn = httpsCallable<
        { projectId: string; companyId?: string; message: string; history: { role: string; content: string }[] },
        ChatResponse
      >(functions, 'chatWithMinutes');
      const payload: { projectId: string; companyId?: string; message: string; history: { role: string; content: string }[] } =
        { projectId: project.id, message: trimmed, history };
      if (project.companyId) payload.companyId = project.companyId;
      const result = await fn(payload);
      const data = result.data;
      setMsgs((m) => [...m, {
        role: 'assistant',
        content: data.text || '（回答を取得できませんでした）',
        sources: Array.isArray(data.sources) ? data.sources : [],
      }]);
    } catch (e: any) {
      console.error('[ChatPanel] chatWithMinutes failed:', e);
      const message: string = e?.message || String(e);
      if (message.includes('PLAN_REQUIRED_STANDARD')) {
        setPlanBlocked(true);
      } else if (message.includes('PLAN_CHECK_FAILED')) {
        setError('プラン情報の取得に失敗しました。再ログインしてからお試しください。');
      } else {
        setError(`回答の取得に失敗しました: ${message}`);
      }
      // 失敗した質問は履歴から取り除き、再送できるよう入力欄へ戻す。
      setMsgs((m) => m.filter((x) => x !== userMessage));
      setInput(trimmed);
    } finally {
      setThinking(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', right: 24, bottom: 24, zIndex: 60,
      width: 420, maxWidth: 'calc(100vw - 48px)', height: 600, maxHeight: 'calc(100vh - 120px)',
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg-popover)', borderRadius: 'var(--r-4)',
      border: '1px solid var(--border-strong)', boxShadow: 'var(--shadow-3)',
      overflow: 'hidden', animation: 'vlrise var(--dur-slow) var(--ease)',
    }}>
      {/* ヘッダ */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
        borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)',
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8, background: 'var(--accent-fill)',
          display: 'grid', placeItems: 'center', color: 'var(--accent)',
        }}>
          <Icon name="sparkles" size={17} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: '500 14px/1.3 var(--font-sans)', color: 'var(--fg)' }}>{isCompanyMode ? '会社AIチャット' : '案件AIチャット'}</div>
          <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {project.name}{project.meetingCount != null ? (isCompanyMode ? ` · ${project.meetingCount}件の打ち合わせ` : ` · 全${project.meetingCount}会議を横断`) : ''}
          </div>
        </div>
        <button onClick={onClose} aria-label="閉じる" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-tertiary)', padding: 4 }}>
          <Icon name="x" size={18} />
        </button>
      </div>

      {/* 本文 */}
      <div ref={bodyRef} style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {msgs.length === 0 && !thinking && (
          <div style={{ margin: 'auto 0', textAlign: 'center', padding: '20px 8px' }}>
            <div style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)', fontSize: 14, lineHeight: 1.7, marginBottom: 18 }}>
              {isCompanyMode ? 'この会社の打ち合わせをまたいで聞けます。' : 'この案件の全打合せをまたいで聞けます。'}<br />回答には必ず出典が付きます。
            </div>
            <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
              ワンタップで聞く
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {CHAT_SKILLS.map((s) => (
                <button key={s.id} onClick={() => ask(s.label)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer',
                  borderRadius: 'var(--r-2)', background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
                  color: 'var(--fg)', font: 'var(--type-ui)', textAlign: 'left',
                }}>
                  <Icon name={s.icon} size={16} style={{ color: 'var(--accent)' }} />
                  <span style={{ flex: 1 }}>{s.label}</span>
                  <Icon name="arrow-up-right" size={14} style={{ color: 'var(--fg-tertiary)' }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {msgs.map((m, i) => m.role === 'user' ? (
          <div key={i} style={{
            alignSelf: 'flex-end', maxWidth: '85%', padding: '10px 14px',
            borderRadius: '14px 14px 4px 14px', background: 'var(--accent)', color: 'var(--navy-900)',
            font: 'var(--type-body)', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap',
          }}>
            {m.content}
          </div>
        ) : (
          <div key={i} style={{ alignSelf: 'flex-start', maxWidth: '92%' }}>
            <div style={{
              padding: '12px 14px', borderRadius: '14px 14px 14px 4px',
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              font: 'var(--type-body)', fontSize: 14, lineHeight: 1.7, color: 'var(--fg-secondary)', whiteSpace: 'pre-wrap',
            }}>
              {m.content}
            </div>
            {m.sources && m.sources.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <span style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)' }}>出典</span>
                {m.sources.map((s, j) => (
                  <SourceChip
                    key={j}
                    label={s.meetingTitle || '会議'}
                    onClick={s.sourceMeetingId ? () => onJump(s.sourceMeetingId as string) : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        {thinking && (
          <div style={{
            alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 14px', borderRadius: 14, background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          }}>
            <span className="vl-typing" /><span className="vl-typing" style={{ animationDelay: '0.2s' }} /><span className="vl-typing" style={{ animationDelay: '0.4s' }} />
          </div>
        )}

        {error && (
          <div style={{
            alignSelf: 'stretch', padding: '10px 14px', borderRadius: 'var(--r-2)',
            background: 'var(--danger-fill)', border: '1px solid var(--danger-line)',
            color: 'var(--danger)', font: 'var(--type-caption)', lineHeight: 1.6,
          }}>
            {error}
          </div>
        )}

        {planBlocked && (
          <div style={{
            alignSelf: 'stretch', padding: 16, borderRadius: 'var(--r-2)',
            background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)', textAlign: 'center',
          }}>
            <div style={{ font: 'var(--type-ui)', color: 'var(--fg)', marginBottom: 8 }}>
              {isCompanyMode ? 'スタンダードでこの会社の打ち合わせをAIに聞けます' : 'スタンダードでこの案件の全会議をAIに聞けます'}
            </div>
            <a href="/pricing" style={{ font: 'var(--type-ui)', color: 'var(--cta)', fontWeight: 600 }}>プランを見る</a>
          </div>
        )}
      </div>

      {/* スキルポップ */}
      {showSkills && (
        <div style={{
          padding: '8px 12px', borderTop: '1px solid var(--border)', background: 'var(--bg-elevated)',
          display: 'flex', flexWrap: 'wrap', gap: 6,
        }}>
          {CHAT_SKILLS.map((s) => (
            <button key={s.id} onClick={() => ask(s.label)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', cursor: 'pointer',
              borderRadius: 'var(--r-pill)', background: 'var(--accent-fill)', border: '1px solid var(--border-accent)',
              color: 'var(--accent)', font: 'var(--type-caption)', fontWeight: 500,
            }}>
              <Icon name={s.icon} size={13} />{s.label}
            </button>
          ))}
        </div>
      )}

      {/* 入力欄 */}
      <div style={{
        padding: 12, borderTop: '1px solid var(--border)', background: 'var(--bg-elevated)',
        display: 'flex', alignItems: 'flex-end', gap: 8,
      }}>
        <button onClick={() => setShowSkills((s) => !s)} title="スキル" style={{
          width: 38, height: 38, flexShrink: 0, borderRadius: 'var(--r-2)', cursor: 'pointer',
          background: showSkills ? 'var(--accent-fill)' : 'var(--bg-hover)',
          border: '1px solid ' + (showSkills ? 'var(--border-accent)' : 'var(--border-strong)'),
          color: showSkills ? 'var(--accent)' : 'var(--fg-secondary)', display: 'grid', placeItems: 'center',
          font: '600 16px/1 var(--font-sans)',
        }}>/</button>
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); ask(input); }
          }}
          placeholder={`${scopeWord}について聞く…`}
          style={{
            flex: 1, resize: 'none', maxHeight: 96, padding: '9px 12px', borderRadius: 'var(--r-2)',
            background: 'var(--bg-hover)', border: '1px solid var(--border-strong)', color: 'var(--fg)',
            font: 'var(--type-body)', fontSize: 14, outline: 'none', lineHeight: 1.5, fontFamily: 'var(--font-sans)',
          }} />
        <button onClick={() => ask(input)} disabled={!input.trim() || thinking} style={{
          width: 38, height: 38, flexShrink: 0, borderRadius: 'var(--r-2)', cursor: input.trim() && !thinking ? 'pointer' : 'not-allowed',
          background: input.trim() && !thinking ? 'var(--cta)' : 'var(--bg-hover)', border: 'none',
          color: input.trim() && !thinking ? 'var(--on-cta)' : 'var(--fg-disabled)', display: 'grid', placeItems: 'center',
        }}>
          <Icon name="arrow-up" size={18} stroke={2} />
        </button>
      </div>
    </div>
  );
};

export default ChatPanel;
